import { useState, useEffect } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { 
  Plus, Edit2, Trash2, Star, Eye, EyeOff, Award, MessageSquare,
  User, Building, Video, Image, BookOpen, GraduationCap, Loader2
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "../../components/ui/alert-dialog";

const API = process.env.REACT_APP_BACKEND_URL + "/api";
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("admin_token")}` } });

// Star Rating Component
function StarRating({ rating, onChange, readonly = false }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <button
          key={star}
          type="button"
          onClick={() => !readonly && onChange?.(star)}
          disabled={readonly}
          className={`${readonly ? 'cursor-default' : 'cursor-pointer hover:scale-110'} transition-transform`}
        >
          <Star 
            size={20} 
            className={star <= rating ? "fill-amber-400 text-amber-400" : "text-gray-300"} 
          />
        </button>
      ))}
    </div>
  );
}

// Testimonial Card
function TestimonialCard({ testimonial, onEdit, onDelete, onToggleVisibility, onToggleFeatured }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-white border ${testimonial.is_featured ? 'border-amber-300 ring-2 ring-amber-100' : 'border-gray-200'} p-5 relative`}
    >
      {/* Featured Badge */}
      {testimonial.is_featured && (
        <div className="absolute -top-2 -right-2 bg-amber-400 text-white px-2 py-0.5 text-xs font-bold flex items-center gap-1">
          <Award size={12} />
          Featured
        </div>
      )}

      {/* Header */}
      <div className="flex items-start gap-4 mb-4">
        {testimonial.photo_url ? (
          <img 
            src={testimonial.photo_url} 
            alt={testimonial.name}
            className="w-14 h-14 rounded-full object-cover border-2 border-gray-100"
          />
        ) : (
          <div className="w-14 h-14 rounded-full bg-gradient-to-br from-cyan-500 to-purple-500 flex items-center justify-center">
            <User size={24} className="text-white" />
          </div>
        )}
        <div className="flex-1">
          <h3 className="font-outfit font-bold text-gray-900">{testimonial.name}</h3>
          {(testimonial.role || testimonial.company) && (
            <p className="text-gray-500 text-sm font-dm-sans">
              {testimonial.role}{testimonial.role && testimonial.company && " at "}{testimonial.company}
            </p>
          )}
          <StarRating rating={testimonial.rating} readonly />
        </div>
      </div>

      {/* Review Text */}
      <p className="text-gray-600 font-dm-sans text-sm mb-4 line-clamp-3">
        "{testimonial.review_text}"
      </p>

      {/* Category Badge */}
      <div className="flex items-center gap-2 mb-4">
        <span className={`px-2 py-1 text-xs font-bold ${
          testimonial.category === 'training' ? 'bg-purple-100 text-purple-700' :
          testimonial.category === 'document' ? 'bg-cyan-100 text-cyan-700' :
          'bg-gray-100 text-gray-700'
        }`}>
          {testimonial.category === 'training' && <GraduationCap size={12} className="inline mr-1" />}
          {testimonial.category === 'document' && <BookOpen size={12} className="inline mr-1" />}
          {testimonial.category === 'general' && <MessageSquare size={12} className="inline mr-1" />}
          {testimonial.category?.charAt(0).toUpperCase() + testimonial.category?.slice(1)}
        </span>
        {testimonial.linked_item_name && (
          <span className="text-xs text-gray-500">• {testimonial.linked_item_name}</span>
        )}
        {testimonial.video_url && (
          <span className="text-xs text-blue-500 flex items-center gap-1">
            <Video size={12} /> Video
          </span>
        )}
      </div>

      {/* Actions */}
      <div className="flex items-center justify-between pt-3 border-t border-gray-100">
        <div className="flex gap-2">
          <button
            onClick={() => onToggleVisibility(testimonial)}
            className={`p-2 rounded ${testimonial.is_visible ? 'text-emerald-600 bg-emerald-50' : 'text-gray-400 bg-gray-50'}`}
            title={testimonial.is_visible ? "Visible" : "Hidden"}
          >
            {testimonial.is_visible ? <Eye size={16} /> : <EyeOff size={16} />}
          </button>
          <button
            onClick={() => onToggleFeatured(testimonial)}
            className={`p-2 rounded ${testimonial.is_featured ? 'text-amber-600 bg-amber-50' : 'text-gray-400 bg-gray-50'}`}
            title={testimonial.is_featured ? "Featured" : "Not Featured"}
          >
            <Award size={16} />
          </button>
        </div>
        <div className="flex gap-2">
          <button onClick={() => onEdit(testimonial)} className="p-2 text-gray-500 hover:text-cyan-600 hover:bg-cyan-50 rounded">
            <Edit2 size={16} />
          </button>
          <button onClick={() => onDelete(testimonial.id)} className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded">
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </motion.div>
  );
}

export default function AdminTestimonials() {
  const [testimonials, setTestimonials] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editTestimonial, setEditTestimonial] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("all"); // all, training, document, general, featured

  const [form, setForm] = useState({
    name: "",
    role: "",
    company: "",
    photo_url: "",
    rating: 5,
    review_text: "",
    video_url: "",
    category: "general",
    linked_item_id: "",
    is_featured: false,
    is_visible: true
  });
  const [saveError, setSaveError] = useState("");

  const fetchData = async () => {
    try {
      const [testimonialsRes, trainingsRes, documentsRes] = await Promise.all([
        axios.get(`${API}/admin/testimonials`, authHeader()),
        axios.get(`${API}/admin/trainings`, authHeader()),
        axios.get(`${API}/admin/books`, authHeader())
      ]);
      setTestimonials(testimonialsRes.data);
      setTrainings(trainingsRes.data);
      setDocuments(documentsRes.data);
    } catch (err) {
      console.error("Failed to fetch data:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const resetForm = () => {
    setForm({
      name: "",
      role: "",
      company: "",
      photo_url: "",
      rating: 5,
      review_text: "",
      video_url: "",
      category: "general",
      linked_item_id: "",
      is_featured: false,
      is_visible: true
    });
    setEditTestimonial(null);
  };

  const openCreate = () => {
    resetForm();
    setSaveError("");
    setShowModal(true);
  };

  const openEdit = (testimonial) => {
    setEditTestimonial(testimonial);
    setSaveError("");
    setForm({
      name: testimonial.name || "",
      role: testimonial.role || "",
      company: testimonial.company || "",
      photo_url: testimonial.photo_url || "",
      rating: testimonial.rating || 5,
      review_text: testimonial.review_text || "",
      video_url: testimonial.video_url || "",
      category: testimonial.category || "general",
      linked_item_id: testimonial.linked_item_id || "",
      is_featured: testimonial.is_featured || false,
      is_visible: testimonial.is_visible !== false
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.review_text.trim()) {
      alert("Name and review text are required");
      return;
    }

    setSaving(true);
    setSaveError("");
    try {
      const payload = {
        name: form.name,
        role: form.role || "",
        company: form.company || "",
        photo_url: form.photo_url || "",
        rating: form.rating,
        review_text: form.review_text,
        video_url: form.video_url || "",
        category: form.category,
        linked_item_id: form.linked_item_id || "",
        is_featured: form.is_featured,
        is_visible: form.is_visible
      };

      if (editTestimonial) {
        await axios.put(`${API}/admin/testimonials/${editTestimonial.id}`, payload, authHeader());
      } else {
        await axios.post(`${API}/admin/testimonials`, payload, authHeader());
      }
      setShowModal(false);
      resetForm();
      fetchData();
    } catch (err) {
      const status = err.response?.status || "Network Error";
      const errorMsg = err.response?.data?.detail || err.message || "Unknown error";
      const fullError = `Error ${status}: ${errorMsg}`;
      
      setSaveError(fullError);
      
      if (status === 401) {
        localStorage.removeItem("admin_token");
        window.location.href = "/admin/login";
      }
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/testimonials/${deleteId}`, authHeader());
      setDeleteId(null);
      fetchData();
    } catch (err) {
      alert("Failed to delete testimonial");
    }
  };

  const toggleVisibility = async (testimonial) => {
    try {
      await axios.put(`${API}/admin/testimonials/${testimonial.id}`, {
        is_visible: !testimonial.is_visible
      }, authHeader());
      fetchData();
    } catch (err) {
      console.error("Failed to toggle visibility:", err);
    }
  };

  const toggleFeatured = async (testimonial) => {
    try {
      await axios.put(`${API}/admin/testimonials/${testimonial.id}`, {
        is_featured: !testimonial.is_featured
      }, authHeader());
      fetchData();
    } catch (err) {
      console.error("Failed to toggle featured:", err);
    }
  };

  const filteredTestimonials = testimonials.filter(t => {
    if (filter === "all") return true;
    if (filter === "featured") return t.is_featured;
    return t.category === filter;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-cyan-500" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Version indicator - remove after debugging */}
      <div className="text-xs text-gray-400 text-right">v2.1</div>
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-outfit font-bold text-gray-900">Testimonials</h1>
          <p className="text-gray-500 font-dm-sans">Manage customer reviews and testimonials</p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-bold hover:opacity-90 transition-opacity"
        >
          <Plus size={18} />
          Add Testimonial
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-gray-200 p-4">
          <p className="text-gray-500 text-sm font-dm-sans">Total</p>
          <p className="text-2xl font-outfit font-bold text-gray-900">{testimonials.length}</p>
        </div>
        <div className="bg-white border border-gray-200 p-4">
          <p className="text-gray-500 text-sm font-dm-sans">Featured</p>
          <p className="text-2xl font-outfit font-bold text-amber-600">{testimonials.filter(t => t.is_featured).length}</p>
        </div>
        <div className="bg-white border border-gray-200 p-4">
          <p className="text-gray-500 text-sm font-dm-sans">Training Reviews</p>
          <p className="text-2xl font-outfit font-bold text-purple-600">{testimonials.filter(t => t.category === 'training').length}</p>
        </div>
        <div className="bg-white border border-gray-200 p-4">
          <p className="text-gray-500 text-sm font-dm-sans">Document Reviews</p>
          <p className="text-2xl font-outfit font-bold text-cyan-600">{testimonials.filter(t => t.category === 'document').length}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        {[
          { key: "all", label: "All" },
          { key: "featured", label: "Featured" },
          { key: "training", label: "Training" },
          { key: "document", label: "Document" },
          { key: "general", label: "General" }
        ].map(({ key, label }) => (
          <button
            key={key}
            onClick={() => setFilter(key)}
            className={`px-4 py-2 text-sm font-bold transition-colors ${
              filter === key 
                ? 'bg-gray-900 text-white' 
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Testimonials Grid */}
      {filteredTestimonials.length === 0 ? (
        <div className="text-center py-12 bg-white border border-gray-200">
          <MessageSquare size={48} className="mx-auto text-gray-300 mb-4" />
          <h3 className="text-lg font-outfit font-bold text-gray-900 mb-2">No Testimonials Yet</h3>
          <p className="text-gray-500 font-dm-sans mb-4">Add your first testimonial to showcase customer reviews</p>
          <button
            onClick={openCreate}
            className="px-4 py-2 bg-cyan-500 text-white font-bold hover:bg-cyan-600"
          >
            Add Testimonial
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredTestimonials.map((testimonial) => (
            <TestimonialCard
              key={testimonial.id}
              testimonial={testimonial}
              onEdit={openEdit}
              onDelete={setDeleteId}
              onToggleVisibility={toggleVisibility}
              onToggleFeatured={toggleFeatured}
            />
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-outfit">{editTestimonial ? "Edit Testimonial" : "Add New Testimonial"}</DialogTitle>
            <DialogDescription>
              {editTestimonial ? "Update testimonial details" : "Add a new customer testimonial"}
            </DialogDescription>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Error Display */}
            {saveError && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm rounded">
                <strong>Error:</strong> {saveError}
              </div>
            )}
            
            {/* Name */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Name *</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none"
                placeholder="John Doe"
                required
              />
            </div>

            {/* Role & Company */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Role</label>
                <input
                  type="text"
                  value={form.role}
                  onChange={(e) => setForm({ ...form, role: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none"
                  placeholder="Software Engineer"
                />
              </div>
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">Company</label>
                <input
                  type="text"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none"
                  placeholder="Tech Corp"
                />
              </div>
            </div>

            {/* Photo URL */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Photo URL</label>
              <input
                type="url"
                value={form.photo_url}
                onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none"
                placeholder="https://example.com/photo.jpg"
              />
            </div>

            {/* Rating */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Rating</label>
              <StarRating rating={form.rating} onChange={(r) => setForm({ ...form, rating: r })} />
            </div>

            {/* Review Text */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Review Text *</label>
              <textarea
                value={form.review_text}
                onChange={(e) => setForm({ ...form, review_text: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none resize-none"
                rows={4}
                placeholder="This training was amazing..."
                required
              />
            </div>

            {/* Video URL */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Video Testimonial URL (optional)</label>
              <input
                type="url"
                value={form.video_url}
                onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none"
                placeholder="https://youtube.com/watch?v=..."
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-1">Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value, linked_item_id: "" })}
                className="w-full px-3 py-2 border border-gray-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none"
              >
                <option value="general">General</option>
                <option value="training">Training</option>
                <option value="document">Document</option>
                <option value="website">Website Development</option>
              </select>
            </div>

            {/* Linked Item */}
            {(form.category === "training" || form.category === "document") && (
              <div>
                <label className="block text-sm font-bold text-gray-700 mb-1">
                  Link to {form.category === "training" ? "Training" : "Document"} (optional)
                </label>
                <select
                  value={form.linked_item_id}
                  onChange={(e) => setForm({ ...form, linked_item_id: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 focus:border-cyan-500 focus:ring-1 focus:ring-cyan-500 outline-none"
                >
                  <option value="">-- None --</option>
                  {form.category === "training" && trainings.map((t) => (
                    <option key={t.id} value={t.id}>{t.title}</option>
                  ))}
                  {form.category === "document" && documents.map((d) => (
                    <option key={d.id} value={d.id}>{d.title}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Toggles */}
            <div className="flex gap-6">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_visible}
                  onChange={(e) => setForm({ ...form, is_visible: e.target.checked })}
                  className="w-4 h-4 text-cyan-500"
                />
                <span className="text-sm text-gray-700">Visible</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_featured}
                  onChange={(e) => setForm({ ...form, is_featured: e.target.checked })}
                  className="w-4 h-4 text-amber-500"
                />
                <span className="text-sm text-gray-700">Featured on Landing Page</span>
              </label>
            </div>

            {/* Submit */}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={() => setShowModal(false)}
                className="flex-1 py-2 border border-gray-300 text-gray-700 font-bold hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={saving}
                className="flex-1 py-2 bg-gradient-to-r from-cyan-500 to-purple-500 text-white font-bold hover:opacity-90 disabled:opacity-50"
              >
                {saving ? "Saving..." : editTestimonial ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Testimonial?</AlertDialogTitle>
            <AlertDialogDescription>
              This action cannot be undone. The testimonial will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
